import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DividerModule } from 'primeng/divider';
import { lastValueFrom } from 'rxjs';
import { HasPermissionDirective } from '../../../../../../directives/has-permission.directive';
import { campaignDefinition } from '../../../../../../entity-definitions/campaign.definition';
import { EntityDialogService } from '../../../../../../services/entity-dialog.service';
import { ToastService } from '../../../../../../services/toast.service';
import { CampaignStore } from '../../../../../../stores/campaign.store';
import { Campaign, CampaignStatus } from '../../../../../../types/campaign.interface';
import { EntityDefinition } from '../../../../../../types/entity-definition.interface';
import { isEntityFormDeleteAction } from '../../../../../../types/entity-form-result.interface';
import { CesButtonSecondaryComponent } from '../../../../../ui/ces-buttons';
import { CesProperty } from '../../../../../ui/ces-property/ces-property.component';
import { CesGeneralInfoSkeletonComponent } from '../../../../../ui/skeletons/general-info-skeleton.component';
import { isConflict } from '../../../../../../utils/http-error.utils';
import { CampaignPageStore } from '../sub-services/campaign-page.store';
import { campaignMinuteTakenValidator } from '../sub-services/campaign-timestamp.validator';

@Component({
  selector: 'ces-campaign-general',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DatePipe,
    CesButtonSecondaryComponent,
    CesProperty,
    DividerModule,
    CesGeneralInfoSkeletonComponent,
    HasPermissionDirective,
    TranslocoModule,
  ],
  templateUrl: './campaign-general.component.html',
})
export class CesCampaignGeneralComponent {
  readonly pageStore = inject(CampaignPageStore);
  readonly dialogService = inject(EntityDialogService);
  readonly toastService = inject(ToastService);
  readonly router = inject(Router);
  private readonly translate = inject(TranslocoService);
  private readonly campaignStore = inject(CampaignStore);

  readonly campaignDefinition = campaignDefinition;

  readonly isCampaignDone = computed(() => this.pageStore.currentCampaign()?.status === CampaignStatus.DONE);

  async editCampaign(): Promise<void> {
    const campaign = this.pageStore.currentCampaign();
    if (!campaign) return;

    try {
      await this.pageStore.executeWithLoading(async () => {
        // Load the asset's open campaigns so the timestamp guard can flag a same-minute clash
        // before submit; cached, and the backend 409 stays authoritative for unloaded campaigns.
        await this.campaignStore.getByAssetId(campaign.assetId);
        const result = await lastValueFrom(
          this.dialogService.openEditDialog(campaign, {
            header: this.campaignDefinition.messages.dialogTitles.edit(),
            entityDefinition: this.timestampGuardedDefinition(campaign),
            mode: 'edit',
            additionalData: {},
          }),
        );

        if (!result) return;
        if (isEntityFormDeleteAction(result)) {
          await this.pageStore.deleteCampaignWithHistory(campaign.id);
          void this.router.navigate(['/assets', campaign.assetId, 'monitoring', 'campaigns']);
          this.toastService.success('common.toasts.deleteSuccess');
          return;
        }

        await this.pageStore.updateCampaignWithHistory(campaign.id, result);
      }, 'general');
    } catch (error) {
      console.error(this.translate.translate('common.errors.campaignEditFailed'), error);
      // The 409 timestamp collision is already surfaced by HttpErrorInterceptor; avoid a
      // second generic toast on that path.
      if (!isConflict(error)) {
        this.toastService.error('common.toasts.saveFailed');
      }
    }
  }

  // Clone the definition with a synchronous same-minute guard on the date field so editing onto a
  // taken minute greys Valider and shows the inline error, matching the creation dialog. The edited
  // campaign is excluded so keeping its own slot stays valid.
  private timestampGuardedDefinition(campaign: Campaign): EntityDefinition<Campaign> {
    const fields = (this.campaignDefinition.fields ?? []).map(field =>
      field.field === 'timestamp'
        ? {
            ...field,
            validator: campaignMinuteTakenValidator(() => this.campaignStore.filterByAssetId(campaign.assetId), campaign.id),
            uniqueErrorKey: 'entities.campaign.messages.duplicateTimestamp',
          }
        : field,
    );
    return { ...this.campaignDefinition, fields };
  }
}
